'use client';
import Dialog from '@/components/Dialog';
import Popup from '@/components/Popup';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { useTranslations } from 'next-intl';
import React from 'react';
import { useInviteFriendStore } from '../store/invite-friend-store';

interface RulesDialogProps {
  pointRule: any;
  pointDollar: string | number;
}

const RulesPopup: React.FC<RulesDialogProps> = ({ pointRule, pointDollar }) => {
  const t = useTranslations('account.referAFriend.rulePopup');
  // 获取shareStat命名空间下的翻译
  const tShareStat = useTranslations('account.referAFriend.shareStat');
  const currency = useStore((state) => state.currency);

  // 从 store 获取状态
  const showType = useInviteFriendStore((state) => state.ruleDialogType);
  const isOpen = useInviteFriendStore((state) => state.isOpenRuleDialog);
  const triggerRuleDialog = useInviteFriendStore((state) => state.setTriggerRuleDialog);

  // 计算值
  const recommendGetPoints = pointRule?.refer_order?.value || '';
  const recommendBuyPoints = pointRule?.refer_order?.invitee_value || '';
  const recommendRegPoints = pointRule?.refer_friend?.invitee_value || '';
  const recommendRegLimit = Math.ceil(
    (pointRule?.refer_order?.day_limit || 0) / (pointRule?.refer_order?.value || 1),
  );
  const discountLimit = pointRule?.discount_limit || '';

  const handleClose = () => {
    triggerRuleDialog(false);
  };

  // 文本样式
  const titleStyle = 'font-bs text-[16px] py-5';
  const titleStyleMobile = 'font-bs text-left text-[14px] leading-[14px] mt-0';
  const textStyle = 'text-[14px] leading-[22px]';
  const textStyleMobile = 'text-[13px] leading-[24px] my-[11px]';

  // 规则内容渲染，PC和移动端共用
  const renderRulesContent = () => (
    <>
      <WebOnly>
        <div className="overflow-y-auto">
          <p className={titleStyle}>{t('programSummary')}</p>
          <p className={textStyle}>{t('summaryPart1')}</p>
          <p className={textStyle}>
            {t('summaryPart2', {
              pointDollar: formatPriceNumberWithSymbol(`${pointDollar}`, currency),
              recommendGetPoints,
            })}
          </p>

          <p className={titleStyle}>{t('howToParticipate')}</p>
          <p className={textStyle}>{t('participateStep1')}</p>
          <p className={textStyle}>{t('participateStep2')}</p>

          <p className={titleStyle}>{t('finePrint')}</p>
          <p className={textStyle}>{t('finePrint1')}</p>
          <p className={textStyle}>{t('finePrint2', { recommendRegPoints, recommendBuyPoints })}</p>
          <p className={textStyle}>{t('finePrint3', { recommendRegLimit })}</p>
          <p className={textStyle}>{t('finePrint4')}</p>
          <p className={textStyle}>{t('finePrint5')}</p>

          <p className={titleStyle}>{t('trackingPoints')}</p>
          <p className={textStyle}>{t('trackingInfo1', { discountLimit })}</p>
          <p className={textStyle}>{t('trackingInfo2')}</p>
          <p className={textStyle}>{t('trackingInfo3')}</p>
          <p className={textStyle}>{t('trackingInfo4')}</p>
        </div>
      </WebOnly>

      <WapOnly>
        <div className="max-h-[426px] w-full sm:w-[320px] overflow-y-auto">
          <p className={titleStyleMobile}>{t('programSummary')}</p>
          <p className={textStyleMobile}>{t('summaryPart1')}</p>
          <p className={textStyleMobile}>
            {t('summaryPart2', {
              pointDollar: formatPriceNumberWithSymbol(`${pointDollar}`, currency),
              recommendGetPoints,
            })}
          </p>

          <p className={titleStyleMobile}>{t('howToParticipate')}</p>
          <p className={textStyleMobile}>{t('participateStep1')}</p>
          <p className={textStyleMobile}>{t('participateStep2')}</p>

          <p className={titleStyleMobile}>{t('finePrint')}</p>
          <p className={textStyleMobile}>{t('finePrint1')}</p>
          <p className={textStyleMobile}>
            {t('finePrint2', { recommendRegPoints, recommendBuyPoints })}
          </p>
          <p className={textStyleMobile}>{t('finePrint3', { recommendRegLimit })}</p>
          <p className={textStyleMobile}>{t('finePrint4')}</p>
          <p className={textStyleMobile}>{t('finePrint5')}</p>

          <p className={titleStyleMobile}>{t('trackingPoints')}</p>
          <p className={textStyleMobile}>{t('trackingInfo1', { discountLimit })}</p>
          <p className={textStyleMobile}>{t('trackingInfo2')}</p>
          <p className={textStyleMobile}>{t('trackingInfo3')}</p>
          <p className={textStyleMobile}>{t('trackingInfo4')}</p>
        </div>
      </WapOnly>
    </>
  );

  // 渲染其他类型内容
  const renderFriendsContent = () => (
    <div className="max-h-[426px] w-full sm:w-[320px] overflow-y-auto">
      <p className={textStyleMobile}>{tShareStat('friendsReferredTooltip')}</p>
    </div>
  );

  const renderPointsContent = () => (
    <div className="max-h-[426px] w-full sm:w-[320px] overflow-y-auto">
      <p className={textStyleMobile}>{tShareStat('pointsTooltip', { discountLimit })}</p>
    </div>
  );

  const renderStatusContent = () => (
    <div className="max-h-[426px] w-full sm:w-[320px] overflow-y-auto">
      <p className={textStyleMobile}>{t('statusExplanation.unregistered')}</p>
      <p className={textStyleMobile}>{t('statusExplanation.unverified')}</p>
      <p className={textStyleMobile}>{t('statusExplanation.verified')}</p>
      <p className={textStyleMobile}>{t('statusExplanation.purchased')}</p>
    </div>
  );

  const renderPurchasedContent = () => (
    <div className="max-h-[426px] w-full sm:w-[320px] overflow-y-auto">
      <p className={textStyleMobile}>
        {t('purchasedLimit', { dayLimit: pointRule?.refer_order?.day_limit })}
      </p>
    </div>
  );

  const renderVerifiedContent = () => (
    <div className="max-h-[426px] w-full sm:w-[320px] overflow-y-auto">
      <p className={textStyleMobile}>
        {t('verifiedLimit', { dayLimit: pointRule?.refer_friend?.day_limit })}
      </p>
    </div>
  );

  // 根据showType选择内容
  const renderContent = () => {
    switch (showType) {
      case 'rules':
        return renderRulesContent();
      case 'friends':
        return renderFriendsContent();
      case 'points':
        return renderPointsContent();
      case 'status':
        return renderStatusContent();
      case 'Purchased':
        return renderPurchasedContent();
      case 'Verified':
        return renderVerifiedContent();
      default:
        return null;
    }
  };

  return (
    <>
      <WapOnly>
        <Popup open={isOpen} onClose={handleClose} title={t('title')}>
          {renderContent()}
        </Popup>
      </WapOnly>

      <WebOnly>
        <Dialog.Info open={isOpen} maxWidth="lg" onClose={handleClose} title={t('title')}>
          {renderContent()}
        </Dialog.Info>
      </WebOnly>
    </>
  );
};

export default RulesPopup;
